/**
 * backfill-account-button.tsx — Kéo lịch sử tin nhắn cho CẢ MỘT TÀI KHOẢN Zalo.
 *
 * Quét toàn bộ hội thoại của tài khoản và lấy thêm tin cũ từ Zalo. Chạy NỀN, có
 * thể mất nhiều phút, nên nút hiển thị tiến độ THẬT từ máy chủ (qua socket) thay
 * vì quay vòng vô nghĩa — không có tiến độ thì người dùng bấm lại vì tưởng hỏng.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { Download, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/misc'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { formatNumber } from '@/lib/utils'
import { useBackfillAccount, useBackfillProgress } from '@/hooks/use-zalo-sync'

const MAC_DINH = 200

export function BackfillAccountButton({
  accountId, disabled,
}: { accountId: string; disabled?: boolean }) {
  const { t } = useTranslation('chanZalo')
  const [mo, setMo] = useState(false)
  const [soTin, setSoTin] = useState(String(MAC_DINH))
  const [kemBanBe, setKemBanBe] = useState(false)
  const backfill = useBackfillAccount()
  const tienDo = useBackfillProgress(accountId)

  const dangChay = backfill.isPending || tienDo?.status === 'processing'

  const chay = () => {
    const n = Number.parseInt(soTin, 10)
    const max = Number.isFinite(n) && n > 0 ? n : MAC_DINH
    setMo(false)
    backfill.mutate(
      { accountId, maxMessages: max, includeFriends: kemBanBe },
      {
        onSuccess: () => toast.success(
          t('backfill.started'),
          { duration: 5000 },
        ),
        onError: (err) => toast.error(apiError(err)),
      },
    )
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled || dangChay}
        onClick={() => setMo(true)}
        title={t('backfill.buttonTitle')}
      >
        {dangChay ? <Loader2 className="animate-spin" /> : <Download />}
        {dangChay && tienDo?.total
          ? t('backfill.progress', { current: tienDo.current, total: tienDo.total })
          : dangChay ? t('backfill.running') : t('backfill.label')}
      </Button>

      <Dialog open={mo} onOpenChange={setMo}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('backfill.dialogTitle')}</DialogTitle>
            <DialogDescription>
              <Trans t={t} i18nKey="backfill.dialogDesc" components={{ b: <strong /> }} />
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div className="grid gap-2">
              <Label>{t('backfill.maxLabel')}</Label>
              <Input type="number" min={1} max={2000} value={soTin}
                     onChange={(e) => setSoTin(e.target.value)} />
              <p className="text-xs text-muted-foreground">
                {t('backfill.maxHint', { n: MAC_DINH })}
              </p>
            </div>

            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Label>{t('backfill.friendsLabel')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('backfill.friendsHint')}
                </p>
              </div>
              <Switch checked={kemBanBe} onCheckedChange={setKemBanBe} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setMo(false)}>{t('common:actions.cancel')}</Button>
            <Button onClick={chay} disabled={backfill.isPending}>
              <Download /> {t('backfill.start')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/** Dòng tiến độ chi tiết, hiện dưới tên tài khoản khi đang chạy. */
export function BackfillProgressLine({ accountId }: { accountId: string }) {
  const { t } = useTranslation('chanZalo')
  const p = useBackfillProgress(accountId)
  if (p?.status !== 'processing') return null
  return (
    <span className="text-[11px] text-primary">
      {' · '}{t('backfill.progress', { current: p.current, total: p.total })}
      {p.threadName ? ` — ${p.threadName}` : ''}
      {p.result ? t('backfill.inserted', { n: formatNumber(p.result.totalInserted ?? 0) }) : ''}
    </span>
  )
}
